import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControl, FormControlLabel,
  IconButton, InputLabel, MenuItem, Select, Switch, TextField,
  Typography, Autocomplete,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Swal from 'sweetalert2';
import api from '../adapters/api';

const RULE_TYPES = [
  { value: 'min_order_amount', label: 'ยอดสั่งซื้อขั้นต่ำ' },
  { value: 'min_qty', label: 'จำนวนสินค้าขั้นต่ำ' },
  { value: 'category', label: 'หมวดหมู่สินค้า' },
  { value: 'product_specific', label: 'สินค้าเฉพาะ' },
  { value: 'customer_specific', label: 'ลูกค้าเฉพาะ' },
  { value: 'customer_group', label: 'กลุ่มลูกค้า' },
  { value: 'price_level', label: 'ระดับราคา' },
  { value: 'buy_x_get_y', label: 'ซื้อ X แถม Y' },
  { value: 'tiered', label: 'ส่วนลดขั้นบันได' },
  { value: 'mix_match', label: 'ผสมหลายรายการ' },
  { value: 'bundle', label: 'ซื้อครบชุด' },
  { value: 'product_sale_price', label: 'ราคาลดพิเศษต่อสินค้า' },
];

const DISCOUNT_TYPES = [
  { value: 'percent', label: 'ส่วนลด %' },
  { value: 'fixed_amount', label: 'ส่วนลดคงที่ (บาท)' },
  { value: 'free_gift', label: 'ของแถม' },
  { value: 'price_override', label: 'ราคาพิเศษ' },
];

const DAYS = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'];
const PRICE_LEVELS = [
  { value: 0, label: '0 — สด' },
  { value: 1, label: '1 — 7%' },
  { value: 2, label: '2 — 10%' },
  { value: 3, label: '3 — 12%' },
  { value: 4, label: '4 — 15%' },
];

const emptyForm = {
  name: '', description: '', code: '', is_active: true,
  start_date: '', end_date: '',
  allowed_days_of_week: null, time_start: '', time_end: '',
  allow_stack: false,
  max_uses: '', max_uses_per_cart: '', max_uses_per_customer: '',
  max_uses_per_customer_per_day: '',
  rule_type: 'min_order_amount', rule_value: {},
  discount_type: 'percent', discount_value: 0, max_discount_amount: '',
  scope: 'cart',
  allowed_sale_codes: null, allowed_branch_codes: null,
  allowed_brands: null, allowed_inquiry_types: null,
};

function RuleValueForm({ ruleType, value, onChange }) {
  const set = (key, val) => onChange({ ...value, [key]: val });

  const arrField = (label, key, helper) => (
    <TextField
      fullWidth size="small" label={label} helperText={helper}
      value={Array.isArray(value[key]) ? value[key].join(', ') : (value[key] || '')}
      onChange={e => set(key, e.target.value ? e.target.value.split(',').map(s => s.trim()) : [])}
    />
  );

  switch (ruleType) {
    case 'min_order_amount':
      return <TextField fullWidth size="small" type="number" label="ยอดขั้นต่ำ (บาท)" value={value.min_amount || ''} onChange={e => set('min_amount', Number(e.target.value))} />;
    case 'min_qty':
      return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {arrField('รหัสสินค้า (คั่นด้วย ,)', 'item_codes')}
        <TextField fullWidth size="small" type="number" label="จำนวนขั้นต่ำ" value={value.min_qty || ''} onChange={e => set('min_qty', Number(e.target.value))} />
      </Box>;
    case 'category':
      return arrField('หมวดหมู่ (item_pattern, คั่นด้วย ,)', 'categories');
    case 'product_specific':
      return arrField('รหัสสินค้า (คั่นด้วย ,)', 'item_codes');
    case 'customer_specific':
      return arrField('รหัสลูกค้า (คั่นด้วย ,)', 'cust_codes');
    case 'customer_group':
      return arrField('ประเภทลูกค้า (ar_type, คั่นด้วย ,)', 'ar_types');
    case 'price_level':
      return <Autocomplete
        multiple size="small"
        options={PRICE_LEVELS}
        getOptionLabel={o => o.label}
        value={PRICE_LEVELS.filter(l => (value.levels || []).includes(l.value))}
        onChange={(_, v) => set('levels', v.map(x => x.value))}
        renderInput={p => <TextField {...p} label="ระดับราคา" />}
      />;
    case 'buy_x_get_y':
      return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {arrField('รหัสสินค้าที่ต้องซื้อ (คั่นด้วย ,)', 'trigger_item_codes')}
        <TextField fullWidth size="small" type="number" label="จำนวนที่ต้องซื้อ" value={value.trigger_qty || ''} onChange={e => set('trigger_qty', Number(e.target.value))} />
        <TextField fullWidth size="small" label="รหัสสินค้าแถม" value={value.gift_item_code || ''} onChange={e => set('gift_item_code', e.target.value)} />
        <TextField fullWidth size="small" type="number" label="จำนวนแถม" value={value.gift_qty || ''} onChange={e => set('gift_qty', Number(e.target.value))} />
        <TextField fullWidth size="small" label="หน่วยของแถม" value={value.gift_unit || ''} onChange={e => set('gift_unit', e.target.value)} />
      </Box>;
    case 'tiered': {
      const tiers = value.tiers || [{ min_amount: 0, discount_value: 0 }];
      return <Box>
        <Typography variant="caption" color="text.secondary">ขั้นบันไดส่วนลด</Typography>
        {tiers.map((t, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
            <TextField size="small" type="number" label="ยอดขั้นต่ำ" value={t.min_amount} onChange={e => { const n = [...tiers]; n[i] = { ...n[i], min_amount: Number(e.target.value) }; set('tiers', n); }} />
            <TextField size="small" type="number" label="ส่วนลด" value={t.discount_value} onChange={e => { const n = [...tiers]; n[i] = { ...n[i], discount_value: Number(e.target.value) }; set('tiers', n); }} />
            <IconButton size="small" onClick={() => set('tiers', tiers.filter((_, j) => j !== i))}><DeleteIcon fontSize="small" /></IconButton>
          </Box>
        ))}
        <Button size="small" onClick={() => set('tiers', [...tiers, { min_amount: 0, discount_value: 0 }])} sx={{ mt: 0.5 }}>+ เพิ่มขั้น</Button>
      </Box>;
    }
    case 'mix_match':
      return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {arrField('รหัสสินค้า (คั่นด้วย ,)', 'item_codes')}
        {arrField('หมวดหมู่ (คั่นด้วย ,)', 'categories')}
        <TextField fullWidth size="small" type="number" label="จำนวนรวมขั้นต่ำ" value={value.min_total_qty || ''} onChange={e => set('min_total_qty', Number(e.target.value))} />
      </Box>;
    case 'bundle': {
      const groups = value.required_groups || [{ item_codes: [], categories: [] }];
      return <Box>
        <Typography variant="caption" color="text.secondary">กลุ่มสินค้าที่ต้องครบ</Typography>
        {groups.map((g, i) => (
          <Box key={i} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1, mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <TextField size="small" label={`กลุ่ม ${i + 1} — รหัสสินค้า (คั่นด้วย ,)`} value={(g.item_codes || []).join(', ')} onChange={e => { const n = [...groups]; n[i] = { ...n[i], item_codes: e.target.value.split(',').map(s => s.trim()) }; set('required_groups', n); }} />
            <TextField size="small" label={`กลุ่ม ${i + 1} — หมวดหมู่ (คั่นด้วย ,)`} value={(g.categories || []).join(', ')} onChange={e => { const n = [...groups]; n[i] = { ...n[i], categories: e.target.value.split(',').map(s => s.trim()) }; set('required_groups', n); }} />
            <Button size="small" color="error" onClick={() => set('required_groups', groups.filter((_, j) => j !== i))}>ลบกลุ่ม</Button>
          </Box>
        ))}
        <Button size="small" onClick={() => set('required_groups', [...groups, { item_codes: [], categories: [] }])} sx={{ mt: 0.5 }}>+ เพิ่มกลุ่ม</Button>
      </Box>;
    }
    case 'product_sale_price': {
      const saleItems = value.items || [{ item_code: '', sale_price: 0 }];
      return <Box>
        <Typography variant="caption" color="text.secondary">ราคาลดพิเศษต่อสินค้า</Typography>
        {saleItems.map((si, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
            <TextField size="small" label="รหัสสินค้า" value={si.item_code} onChange={e => { const n = [...saleItems]; n[i] = { ...n[i], item_code: e.target.value }; set('items', n); }} />
            <TextField size="small" type="number" label="ราคาพิเศษ" value={si.sale_price} onChange={e => { const n = [...saleItems]; n[i] = { ...n[i], sale_price: Number(e.target.value) }; set('items', n); }} />
            <IconButton size="small" onClick={() => set('items', saleItems.filter((_, j) => j !== i))}><DeleteIcon fontSize="small" /></IconButton>
          </Box>
        ))}
        <Button size="small" onClick={() => set('items', [...saleItems, { item_code: '', sale_price: 0 }])} sx={{ mt: 0.5 }}>+ เพิ่มสินค้า</Button>
      </Box>;
    }
    default:
      return null;
  }
}

function PromotionFormDialog({ open, onClose, onSaved, initial }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          ...emptyForm,
          ...initial,
          start_date: initial.start_date ? String(initial.start_date).slice(0, 10) : '',
          end_date: initial.end_date ? String(initial.end_date).slice(0, 10) : '',
        });
      } else {
        setForm(emptyForm);
      }
    }
  }, [open, initial]);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = async () => {
    if (!form.name || !form.rule_type) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        max_uses: form.max_uses ? Number(form.max_uses) : null,
        max_uses_per_cart: form.max_uses_per_cart ? Number(form.max_uses_per_cart) : null,
        max_uses_per_customer: form.max_uses_per_customer ? Number(form.max_uses_per_customer) : null,
        max_uses_per_customer_per_day: form.max_uses_per_customer_per_day ? Number(form.max_uses_per_customer_per_day) : null,
        discount_value: Number(form.discount_value) || 0,
        max_discount_amount: form.max_discount_amount ? Number(form.max_discount_amount) : null,
        code: form.code ? form.code.toUpperCase() : null,
      };
      const endpoint = form.id ? '/api/promotions/update' : '/api/promotions/create';
      await api.post(endpoint, payload, { withCredentials: true });
      onSaved();
      onClose();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: err.response?.data?.error || 'ไม่สามารถบันทึกได้', confirmButtonColor: '#1976d2', confirmButtonText: 'ตกลง' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{form.id ? 'แก้ไขโปรโมชัน' : 'สร้างโปรโมชันใหม่'}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {/* ข้อมูลพื้นฐาน */}
          <TextField required fullWidth size="small" label="ชื่อโปรโมชัน" value={form.name} onChange={e => set('name', e.target.value)} />
          <TextField fullWidth size="small" label="คำอธิบาย" multiline rows={2} value={form.description} onChange={e => set('description', e.target.value)} />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth size="small" label="โค้ดโปรโมชัน (ว่างคือ auto-apply)" value={form.code} onChange={e => set('code', e.target.value.toUpperCase())} inputProps={{ style: { textTransform: 'uppercase' } }} />
            <FormControlLabel control={<Switch checked={form.is_active} onChange={e => set('is_active', e.target.checked)} />} label="เปิดใช้งาน" />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth size="small" type="date" label="วันเริ่มต้น" InputLabelProps={{ shrink: true }} value={form.start_date || ''} onChange={e => set('start_date', e.target.value)} />
            <TextField fullWidth size="small" type="date" label="วันสิ้นสุด" InputLabelProps={{ shrink: true }} value={form.end_date || ''} onChange={e => set('end_date', e.target.value)} />
          </Box>

          <Divider><Typography variant="caption">วัน/เวลา</Typography></Divider>
          <Autocomplete
            multiple size="small"
            options={DAYS.map((d, i) => ({ label: d, value: i }))}
            getOptionLabel={o => o.label}
            value={(form.allowed_days_of_week || []).map(v => ({ label: DAYS[v], value: v }))}
            onChange={(_, v) => set('allowed_days_of_week', v.length ? v.map(x => x.value) : null)}
            renderInput={p => <TextField {...p} label="วันที่อนุญาต (ว่าง = ทุกวัน)" />}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth size="small" type="time" label="เวลาเริ่ม" InputLabelProps={{ shrink: true }} value={form.time_start || ''} onChange={e => set('time_start', e.target.value || null)} />
            <TextField fullWidth size="small" type="time" label="เวลาสิ้นสุด" InputLabelProps={{ shrink: true }} value={form.time_end || ''} onChange={e => set('time_end', e.target.value || null)} />
          </Box>

          <Divider><Typography variant="caption">จำกัดการใช้งาน</Typography></Divider>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField size="small" type="number" label="สูงสุดทั้งหมด (ว่าง=ไม่จำกัด)" value={form.max_uses} onChange={e => set('max_uses', e.target.value)} sx={{ flex: 1 }} />
            <TextField size="small" type="number" label="สูงสุดต่อบิล" value={form.max_uses_per_cart} onChange={e => set('max_uses_per_cart', e.target.value)} sx={{ flex: 1 }} />
            <TextField size="small" type="number" label="สูงสุดต่อลูกค้า" value={form.max_uses_per_customer} onChange={e => set('max_uses_per_customer', e.target.value)} sx={{ flex: 1 }} />
            <TextField size="small" type="number" label="สูงสุด/วัน/ลูกค้า" value={form.max_uses_per_customer_per_day} onChange={e => set('max_uses_per_customer_per_day', e.target.value)} sx={{ flex: 1 }} />
          </Box>
          <FormControlLabel control={<Switch checked={form.allow_stack} onChange={e => set('allow_stack', e.target.checked)} />} label="ใช้ร่วมกับโปรโมชันอื่นได้" />

          <Divider><Typography variant="caption">เงื่อนไขโปรโมชัน</Typography></Divider>
          <FormControl size="small" fullWidth>
            <InputLabel>ประเภทเงื่อนไข</InputLabel>
            <Select label="ประเภทเงื่อนไข" value={form.rule_type} onChange={e => set('rule_type', e.target.value)}>
              {RULE_TYPES.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
            </Select>
          </FormControl>
          <RuleValueForm ruleType={form.rule_type} value={form.rule_value || {}} onChange={v => set('rule_value', v)} />

          <Divider><Typography variant="caption">ส่วนลด</Typography></Divider>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>ประเภทส่วนลด</InputLabel>
              <Select label="ประเภทส่วนลด" value={form.discount_type} onChange={e => set('discount_type', e.target.value)}>
                {DISCOUNT_TYPES.map(d => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField size="small" type="number" label="ค่าส่วนลด" value={form.discount_value} onChange={e => set('discount_value', e.target.value)} sx={{ flex: 1 }} />
            <TextField size="small" type="number" label="สูงสุด (บาท)" helperText="ว่าง = ไม่จำกัด" value={form.max_discount_amount} onChange={e => set('max_discount_amount', e.target.value)} sx={{ flex: 1 }} />
          </Box>
          <FormControl size="small" fullWidth>
            <InputLabel>ขอบเขต</InputLabel>
            <Select label="ขอบเขต" value={form.scope} onChange={e => set('scope', e.target.value)}>
              <MenuItem value="cart">ทั้งบิล (cart)</MenuItem>
              <MenuItem value="line">ต่อรายการ (line)</MenuItem>
            </Select>
          </FormControl>

          <Divider><Typography variant="caption">ข้อจำกัดเพิ่มเติม</Typography></Divider>
          <TextField fullWidth size="small" label="รหัสพนักงานขาย (คั่นด้วย , ว่าง=ทั้งหมด)"
            value={(form.allowed_sale_codes || []).join(', ')}
            onChange={e => set('allowed_sale_codes', e.target.value ? e.target.value.split(',').map(s => s.trim()) : null)} />
          <TextField fullWidth size="small" label="รหัสสาขา (คั่นด้วย , ว่าง=ทั้งหมด)"
            value={(form.allowed_branch_codes || []).join(', ')}
            onChange={e => set('allowed_branch_codes', e.target.value ? e.target.value.split(',').map(s => s.trim()) : null)} />
          <TextField fullWidth size="small" label="แบรนด์สินค้า (คั่นด้วย , ว่าง=ทั้งหมด)"
            helperText="ต้องมีสินค้าแบรนด์นี้อย่างน้อย 1 รายการในบิล"
            value={(form.allowed_brands || []).join(', ')}
            onChange={e => set('allowed_brands', e.target.value ? e.target.value.split(',').map(s => s.trim()) : null)} />
          <Autocomplete
            multiple size="small"
            options={[{ label: 'เงินสด (1)', value: 1 }, { label: 'เครดิต (0)', value: 0 }]}
            getOptionLabel={o => o.label}
            value={(form.allowed_inquiry_types || []).map(v => v === 1 ? { label: 'เงินสด (1)', value: 1 } : { label: 'เครดิต (0)', value: 0 })}
            onChange={(_, v) => set('allowed_inquiry_types', v.length ? v.map(x => x.value) : null)}
            renderInput={p => <TextField {...p} label="ประเภทบิล (ว่าง=ทั้งหมด)" />}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ color: '#757575' }}>ยกเลิก</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ bgcolor: '#1976d2' }}>
          {saving ? <CircularProgress size={18} /> : 'บันทึก'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function Promotions() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterActive, setFilterActive] = useState('');
  const [page, setPage] = useState(0);
  const pageSize = 50;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const payload = { page: page + 1, page_size: pageSize };
      if (search) payload.search = search;
      if (filterActive !== '') payload.is_active = filterActive === 'true';
      const { data } = await api.post('/api/promotions/list', payload, { withCredentials: true });
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, filterActive, page]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      icon: 'warning', title: 'ยืนยันการปิดใช้งาน',
      text: 'โปรโมชันนี้จะถูกปิดใช้งาน (ไม่ถูกลบออกจากระบบ)',
      showCancelButton: true, confirmButtonColor: '#d32f2f', cancelButtonColor: '#757575',
      confirmButtonText: 'ปิดใช้งาน', cancelButtonText: 'ยกเลิก',
    });
    if (!result.isConfirmed) return;
    await api.post('/api/promotions/delete', { id }, { withCredentials: true });
    load();
  };

  const columns = [
    { field: 'name', headerName: 'ชื่อโปรโมชัน', flex: 2 },
    { field: 'code', headerName: 'โค้ด', width: 110, renderCell: ({ value }) => value ? <Chip label={value} size="small" variant="outlined" /> : <Typography sx={{ fontSize: 12, color: '#aaa' }}>auto</Typography> },
    { field: 'rule_type', headerName: 'เงื่อนไข', width: 140, renderCell: ({ value }) => <Typography sx={{ fontSize: 12 }}>{RULE_TYPES.find(r => r.value === value)?.label || value}</Typography> },
    { field: 'discount_type', headerName: 'ส่วนลด', width: 130, renderCell: ({ row }) => <Typography sx={{ fontSize: 12 }}>{DISCOUNT_TYPES.find(d => d.value === row.discount_type)?.label} {row.discount_value > 0 ? row.discount_value : ''}</Typography> },
    { field: 'use_count', headerName: 'ใช้แล้ว', width: 80, type: 'number' },
    { field: 'is_active', headerName: 'สถานะ', width: 90, renderCell: ({ value }) => <Chip label={value ? 'เปิด' : 'ปิด'} size="small" color={value ? 'success' : 'default'} /> },
    { field: 'start_date', headerName: 'วันเริ่ม', width: 105, valueFormatter: (value) => value ? String(value).slice(0, 10) : '-' },
    { field: 'end_date', headerName: 'วันสิ้นสุด', width: 105, valueFormatter: (value) => value ? String(value).slice(0, 10) : '-' },
    {
      field: 'actions', headerName: '', width: 90, sortable: false,
      renderCell: ({ row }) => (
        <Box>
          <IconButton size="small" onClick={() => { setEditTarget(row); setDialogOpen(true); }}><EditIcon fontSize="small" /></IconButton>
          <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}><DeleteIcon fontSize="small" /></IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
        <Typography variant="h6" fontWeight={600}>โปรโมชัน</Typography>
        <TextField size="small" placeholder="ค้นหาชื่อ / โค้ด" value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} sx={{ width: 200 }} />
        <FormControl size="small" sx={{ width: 140 }}>
          <InputLabel>สถานะ</InputLabel>
          <Select label="สถานะ" value={filterActive} onChange={e => { setFilterActive(e.target.value); setPage(0); }}>
            <MenuItem value="">ทั้งหมด</MenuItem>
            <MenuItem value="true">เปิดใช้งาน</MenuItem>
            <MenuItem value="false">ปิดใช้งาน</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ ml: 'auto' }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditTarget(null); setDialogOpen(true); }}>
            สร้างโปรโมชันใหม่
          </Button>
        </Box>
      </Box>

      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        rowCount={total}
        paginationMode="server"
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={({ page: p }) => setPage(p)}
        disableRowSelectionOnClick
        sx={{ flex: 1 }}
      />

      <PromotionFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={load}
        initial={editTarget}
      />
    </Box>
  );
}
